import "./styles.scss";

import { useRef, useState } from "react";
import CustomModal from "@components/CustomModal";
import { Input, Text, Icon } from "@fragments";
import {
  IconCheck,
  IconX,
  IconBan,
  IconAlertTriangle,
} from "@tabler/icons-react";

import api from "@helpers/api";

// One modal, three modes (§6.7 accept/reject · §6.8 end). `notesRequired` gates
// the submit; `tone` paints the confirm button + the header accent.
const ACTION_CONFIG = {
  accept: {
    title: "Aprovar candidatura",
    submit: "Aprovar candidatura",
    color: "success",
    tone: "success",
    icon: IconCheck,
    notesLabel: "Observações (opcional)",
    notesPlaceholder: "Recado interno ou para o criador",
    notesRequired: false,
    lead: (name) =>
      `Aprovar ${name} no programa. O criador poderá vincular uma comunidade para iniciar a mentoria.`,
    toast: "Candidatura aprovada",
  },
  reject: {
    title: "Recusar candidatura",
    submit: "Recusar candidatura",
    color: "error",
    tone: "error",
    icon: IconX,
    notesLabel: "Motivo da recusa",
    notesPlaceholder: "Explique por que a candidatura não foi aprovada",
    notesRequired: true,
    lead: (name) =>
      `Recusar a candidatura de ${name}. O criador será notificado com o motivo informado.`,
    toast: "Candidatura recusada",
  },
  end: {
    title: "Encerrar mentoria",
    submit: "Encerrar mentoria",
    color: "error",
    tone: "error",
    icon: IconBan,
    notesLabel: "Observações (opcional)",
    notesPlaceholder: "Registre o motivo do encerramento",
    notesRequired: false,
    warning:
      "O repasse de receita é removido da comunidade do criador imediatamente e ele é notificado. Esta ação não pode ser desfeita.",
    lead: (name) => `Encerrar antecipadamente a mentoria de ${name}.`,
    toast: "Mentoria encerrada",
  },
};

/**
 * Review / end enrollment actions — §6.7–6.8. Imperative open:
 *   modalRef.current.open({ enrollment, action: 'accept'|'reject'|'end', onDone })
 * Reject requires a reason; end shows a destructive warning. 422 → inline error
 * on the notes field + toast.
 */
export default function EnrollmentActionModal({ modalRef, communityId }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [action, setAction] = useState("accept");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState("");

  const enrollmentRef = useRef(null);
  const onDoneRef = useRef(null);

  modalRef.current = {
    open({ enrollment, action: nextAction, onDone }) {
      enrollmentRef.current = enrollment ?? null;
      onDoneRef.current = onDone;
      setAction(nextAction ?? "accept");
      setNotes("");
      setError("");
      setOpen(true);
    },
  };

  const cfg = ACTION_CONFIG[action] ?? ACTION_CONFIG.accept;
  const enrollment = enrollmentRef.current;
  const name = enrollment
    ? enrollment.creator?.name || enrollment.invited_email
    : "este criador";
  const HeadIcon = cfg.icon;

  async function handleSubmit() {
    const trimmed = notes.trim();
    if (cfg.notesRequired && !trimmed) {
      setError(`Informe ${cfg.notesLabel.toLowerCase()}`);
      return;
    }

    setLoading(true);
    setError("");

    // Review/end live under the program scope; list rows and the detail both
    // carry program_id (the list adapter injects it from the route).
    const base = `community/${communityId}/mentoring/programs/${enrollment?.program_id}/enrollments/${enrollment?.id}`;
    const res =
      action === "end"
        ? await api.delete(base, { notes: trimmed || null })
        : await api.patch(`${base}/review`, { action, notes: trimmed || null });

    setLoading(false);

    if (res?.response?.status >= 400) {
      const fieldError = res.response.data?.errors?.notes?.[0];
      if (fieldError) setError(fieldError);
      window.setSnackbar?.(
        res.response.data?.message || "Não foi possível concluir a ação",
      );
      return;
    }

    window.setSnackbar?.(cfg.toast);
    onDoneRef.current?.(res.data);
    setOpen(false);
  }

  return (
    <CustomModal
      maxWidth={480}
      open={open}
      handleClose={() => setOpen(false)}
      title={cfg.title}
      bottomButtonSumbitText={cfg.submit}
      mainButtonColor={cfg.color}
      handleSubmit={handleSubmit}
      loading={loading}
      bodyComponent={
        open ? (
          <div className="enr-action-modal">
            <div className={`eam-head eam-head--${cfg.tone}`}>
              <Icon.Sphere children={<HeadIcon size={20} />} />
              <Text.span contrast={8}>{cfg.lead(name)}</Text.span>
            </div>

            {cfg.warning && (
              <div className="eam-warning">
                <IconAlertTriangle size={18} />
                <Text.span contrast={8}>{cfg.warning}</Text.span>
              </div>
            )}

            <Input.Text
              label={cfg.notesLabel}
              value={notes}
              onInput={(v) => {
                setNotes(v);
                if (error) setError("");
              }}
              multiline
              autoFocus
              error={!!error}
              helper={error}
              placeholder={cfg.notesPlaceholder}
            />
          </div>
        ) : null
      }
    />
  );
}
